// Shop buy modal — the in-page confirm/success flow the /shop hero's
// single primary "Buy sealed" opens (Epic 8 design pass).
//
// This is the SHOP surface's restyle of the purchase-confirm flow to the
// two-step design spec (CONFIRM PURCHASE → POURED · REGISTRY RECORDED). It
// deliberately does NOT touch the shared <PurchaseConfirmModal> (still used
// by the /account/buy/:dropId deep-link page, which keeps its always-visible
// balance breakdown per UX-DR24). Everything transactional is the REAL
// thing: it drives useDossierPurchase (POST /api/v1/dossiers/:dropId/purchase),
// mints a fresh idempotency key per submit, and branches the backend's error
// codes — the insufficient-balance interrupt hands off to the real TopUpModal
// and resumes.
//
// Registry voice throughout: "pour", "seal", "registry-recorded".

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";

import { Button, formatIssueNumber } from "@greenhold/components";

import { TopUpModal } from "../top-up/TopUpModal";
import {
  MAX_PURCHASE_QUANTITY,
  useDossierPurchase,
} from "../dossier-purchase/useDossierPurchase";

const EUR = new Intl.NumberFormat("en-IE", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 2,
});

function formatEur(cents: number): string {
  return EUR.format(cents / 100);
}

type Step = "confirm" | "submitting" | "success" | "error" | "cap" | "closed";

export interface ShopBuyModalProps {
  open: boolean;
  onClose: () => void;
  dropId: string;
  /** Portfolio title of the highlighted dossier (hero name). */
  title: string;
  priceCents: number;
  /** Special-drop per-user cap when known — bounds the quantity stepper.
   *  The backend cap check stays authoritative. */
  perUserCap?: number | null;
}

export function ShopBuyModal({
  open,
  onClose,
  dropId,
  title,
  priceCents,
  perUserCap,
}: ShopBuyModalProps) {
  const [step, setStep] = useState<Step>("confirm");
  const [quantity, setQuantity] = useState(1);
  const [capRemaining, setCapRemaining] = useState<number | null>(null);
  const [editions, setEditions] = useState<number[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [topupOpen, setTopupOpen] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState(() =>
    crypto.randomUUID(),
  );

  const purchase = useDossierPurchase(dropId);
  const primaryRef = useRef<HTMLButtonElement>(null);

  const maxQuantity = Math.max(
    1,
    Math.min(MAX_PURCHASE_QUANTITY, perUserCap ?? MAX_PURCHASE_QUANTITY),
  );

  // Reset to the confirm step whenever the modal (re)opens for a drop.
  useEffect(() => {
    if (open) {
      setStep("confirm");
      setQuantity(1);
      setEditions([]);
      setErrorMessage(null);
      setIdempotencyKey(crypto.randomUUID());
    }
  }, [open, dropId]);

  // Escape-to-cancel (matches the design's click-outside-cancels affordance).
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Default focus to the primary action per UX-DR §1241.
  useEffect(() => {
    if (open && step === "confirm") primaryRef.current?.focus();
  }, [open, step]);

  if (!open) return null;

  function confirm() {
    setStep("submitting");
    setErrorMessage(null);
    purchase.mutate(
      { idempotencyKey, quantity },
      {
        onSuccess: (data) => {
          const minted = data.dossiers.map((d) => d.edition_number);
          setEditions(minted);
          setStep("success");
          toast.success(
            minted.length === 1
              ? `Issue #${formatIssueNumber(minted[0])} poured to your vault.`
              : `${minted.length} issues (#${formatIssueNumber(minted[0])}–#${formatIssueNumber(minted[minted.length - 1])}) poured to your vault.`,
          );
        },
        onError: (err) => {
          if (err.code === "INSUFFICIENT_BALANCE") {
            // Top-up interrupt — hand off to the real wallet top-up flow and
            // resume on success.
            setStep("confirm");
            setTopupOpen(true);
            return;
          }
          if (err.code === "DOSSIER_PURCHASE_CAP_REACHED") {
            // The checkout is all-or-nothing: asking for more than the
            // remaining headroom 409s even when some headroom is left.
            // Surface the remainder so the user can retry with fewer.
            setCapRemaining(
              err.per_user_cap !== undefined &&
                err.user_owned_count !== undefined
                ? Math.max(0, err.per_user_cap - err.user_owned_count)
                : null,
            );
            setStep("cap");
            return;
          }
          if (err.code === "DOSSIER_PURCHASE_DROP_CLOSED") {
            setStep("closed");
            return;
          }
          if (err.code === "IDEMPOTENCY_CONFLICT") {
            setIdempotencyKey(crypto.randomUUID());
          }
          setErrorMessage(err.message ?? "Something went wrong at the pour.");
          setStep("error");
        },
      },
    );
  }

  return (
    <>
      <div
        className="fixed inset-0 z-50 grid place-items-center bg-brand-forest-900/60 p-4"
        onClick={onClose}
        data-testid="shop-buy-modal"
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Confirm dossier purchase"
          onClick={(e) => e.stopPropagation()}
          className="grid w-[400px] max-w-[90vw] gap-4 rounded-xl border border-brand-paper-300 bg-brand-paper-50 p-8 text-brand-forest-900 shadow-xl"
        >
          {step === "confirm" || step === "submitting" ? (
            <>
              <div className="font-mono text-[0.62rem] uppercase tracking-[0.1em] text-brand-gold-600">
                Confirm purchase
              </div>
              <div className="font-heading text-lg font-semibold">{title}</div>
              <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 font-mono text-[0.72rem]">
                <dt className="self-center opacity-60">Item</dt>
                <dd className="m-0 flex items-center gap-2">
                  <button
                    type="button"
                    aria-label="Fewer dossiers"
                    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                    disabled={quantity <= 1 || step === "submitting"}
                    className="grid h-6 w-6 place-items-center rounded border border-brand-paper-300 leading-none disabled:opacity-30"
                    data-testid="shop-buy-qty-minus"
                  >
                    −
                  </button>
                  <span data-testid="shop-buy-qty" className="min-w-4 text-center">
                    {quantity}
                  </span>
                  <button
                    type="button"
                    aria-label="More dossiers"
                    onClick={() =>
                      setQuantity((q) => Math.min(maxQuantity, q + 1))
                    }
                    disabled={quantity >= maxQuantity || step === "submitting"}
                    className="grid h-6 w-6 place-items-center rounded border border-brand-paper-300 leading-none disabled:opacity-30"
                    data-testid="shop-buy-qty-plus"
                  >
                    +
                  </button>
                  <span>
                    sealed dossier{quantity === 1 ? "" : "s"}
                  </span>
                </dd>
                <dt className="opacity-60">Price</dt>
                <dd className="m-0">
                  {formatEur(priceCents)} · fixed
                  {quantity > 1 ? ` · each` : ""}
                </dd>
                <dt className="opacity-60">Pour</dt>
                <dd className="m-0">registry-recorded at purchase</dd>
              </dl>
              <p className="m-0 text-[0.78rem] opacity-70">
                {quantity === 1
                  ? "The dossier lands sealed in your vault."
                  : "Each dossier lands sealed in your vault."}{" "}
                No refunds after the seal is broken.
              </p>
              <div className="flex justify-end gap-3">
                <Button variant="ghost" size="md" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  ref={primaryRef}
                  variant="primary"
                  size="md"
                  onClick={confirm}
                  disabled={step === "submitting"}
                  data-testid="shop-buy-confirm"
                >
                  {step === "submitting"
                    ? "Pouring…"
                    : `Confirm · ${formatEur(priceCents * quantity)}`}
                </Button>
              </div>
            </>
          ) : null}

          {step === "success" ? (
            <div className="grid gap-4" data-testid="shop-buy-success" role="status">
              <div className="font-mono text-[0.62rem] uppercase tracking-[0.1em] text-brand-forest-600">
                Poured · registry recorded
              </div>
              <div className="font-heading text-lg font-semibold">
                {title}
                {editions.length === 1
                  ? ` — Issue #${formatIssueNumber(editions[0])}`
                  : editions.length > 1
                    ? ` — Issues #${formatIssueNumber(editions[0])}–#${formatIssueNumber(editions[editions.length - 1])}`
                    : ""}
              </div>
              <p className="m-0 text-[0.82rem] opacity-70">
                {editions.length > 1
                  ? "Your sealed dossiers are in your "
                  : "Your sealed dossier is in your "}
                <Link to="/vault" className="text-brand-forest-600 underline-offset-2 hover:underline">
                  vault
                </Link>
                . Break the seal whenever you're ready — the ceremony decides
                the tier.
              </p>
              <div className="flex justify-end gap-3">
                <Button variant="secondary" size="md" onClick={onClose}>
                  Close
                </Button>
              </div>
            </div>
          ) : null}

          {step === "cap" ? (
            <div className="grid gap-4">
              <div className="font-mono text-[0.62rem] uppercase tracking-[0.1em] text-brand-gold-600">
                Per-account limit
              </div>
              <p className="m-0 text-[0.82rem] opacity-70">
                {capRemaining !== null && capRemaining > 0
                  ? `This pour would pass the per-account limit for this dossier — you can still pour up to ${capRemaining} more.`
                  : "You've already poured the per-account limit for this dossier. Holders resell revealed and sealed lots on the market."}
              </p>
              <div className="flex justify-end gap-3">
                {capRemaining !== null && capRemaining > 0 ? (
                  <Button
                    variant="primary"
                    size="md"
                    onClick={() => {
                      setQuantity(Math.min(capRemaining, maxQuantity));
                      setIdempotencyKey(crypto.randomUUID());
                      setStep("confirm");
                    }}
                  >
                    Pour {Math.min(capRemaining, maxQuantity)} instead
                  </Button>
                ) : (
                  <Button asChild variant="secondary" size="md">
                    <Link to={`/marketplace?drop=${dropId}`}>See the market</Link>
                  </Button>
                )}
                <Button variant="ghost" size="md" onClick={onClose}>
                  Close
                </Button>
              </div>
            </div>
          ) : null}

          {step === "closed" ? (
            <div className="grid gap-4">
              <div className="font-mono text-[0.62rem] uppercase tracking-[0.1em] text-brand-gold-600">
                Drop closed
              </div>
              <p className="m-0 text-[0.82rem] opacity-70">
                This dossier has retired from the registry-direct shop. Browse
                the market for holder relistings.
              </p>
              <div className="flex justify-end gap-3">
                <Button variant="secondary" size="md" onClick={onClose}>
                  Close
                </Button>
              </div>
            </div>
          ) : null}

          {step === "error" ? (
            <div className="grid gap-4">
              <div className="font-mono text-[0.62rem] uppercase tracking-[0.1em] text-state-error-accent">
                Pour failed
              </div>
              <p className="m-0 text-[0.82rem] opacity-70">
                {errorMessage ?? "Something went wrong at the pour."}
              </p>
              <div className="flex justify-end gap-3">
                <Button variant="ghost" size="md" onClick={onClose}>
                  Cancel
                </Button>
                <Button variant="primary" size="md" onClick={confirm}>
                  Try again
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <TopUpModal
        open={topupOpen}
        onOpenChange={setTopupOpen}
        onSuccess={() => {
          // Freshly credited — mint a new key and let the user reconfirm.
          setIdempotencyKey(crypto.randomUUID());
          setStep("confirm");
        }}
      />
    </>
  );
}
